import React, { useState } from 'react';
import SimpleBar from 'simplebar-react';

import { Toggle } from 'components/elements/Toggle/Toggle';
import { DownloadMobileAppButton } from 'components/mobileAppPrompts/DownloadMobileAppButton';
import CloseIcon from 'components/newListeningExperience/icons/CloseIcon';
import { useTranslation } from 'hooks/useTypedTranslation';
import { BooleanDisplaySettingKey, skipContentSettingNames } from 'modules/listening/features/settings/settings';
import { useListeningSettingsStore } from 'modules/listening/features/settings/settingsStore';
import { isIOS, isMobile } from 'utils/browser';
import { cn } from 'utils/cn';

import { AppearanceIcon, AutoHideInterfaceIcon, AutoScrollIcon, ChevronRightIcon, ClickToListenIcon } from '../icons';
import AutoSkipContentIcon from '../icons/AutoSkipContentIcon';
import AppearancePanel from './AppearancePanel';
import AutoSkipContentPanel from './AutoSkipContentPanel';

export enum AutoSkipContentUnlockedReason {
  Shared = 'shared',
  Premium = 'premium'
}

enum SettingsPanel {
  Root = 'root',
  AutoSkipContent = 'autoSkipContent',
  Appearance = 'appearance'
}

export type SettingsProps = {
  autoSkipContentUnlocked: boolean;
  autoSkipContentUnlockedReason?: unknown;
  className?: string;
  isEPUB: boolean;
  isPDF: boolean;
  onAllSkipSettingsChange: (checked: boolean) => void;
  onAutoSkipContentChange: (settingName: (typeof skipContentSettingNames)[number], checked: boolean) => void;
  onClose: () => void;
  onSettingChange: (settingName: unknown, checked: boolean) => void;
} & Record<string, unknown>;

const Settings: React.FC<SettingsProps> = ({
  autoSkipContentUnlocked,
  autoSkipContentUnlockedReason,
  className,
  isPDF,
  isEPUB,
  onClose,
  onAllSkipSettingsChange,
  onSettingChange,
  onAutoSkipContentChange,
  ...props
}) => {
  const { t } = useTranslation('common');
  const darkMode = useListeningSettingsStore(s => s.darkMode);

  const [currentPanel, setCurrentPanel] = useState<SettingsPanel>(SettingsPanel.Root);

  const skipContentSettings = useListeningSettingsStore(s => ({
    braces: s.braces,
    brackets: s.brackets,
    citations: s.citations,
    enhancedSkipping: s.enhancedSkipping,
    footers: s.footers,
    footnotes: s.footnotes,
    formulas: s.formulas,
    headers: s.headers,
    parentheses: s.parentheses,
    tables: s.tables,
    urls: s.urls
  }));

  const toggleSettingsConfig: Array<{
    key: BooleanDisplaySettingKey;
    icon: JSX.Element;
    title: string;
    description: string;
  }> = [
    {
      key: 'autoHideInterface',
      icon: <AutoHideInterfaceIcon />,
      title: t('Auto-Hide Player'),
      description: t('Disappears when idle')
    },
    {
      key: 'autoScroll',
      icon: <AutoScrollIcon />,
      title: t('Auto Scroll'),
      description: t('Follows the reading automatically')
    },
    {
      key: 'clickToListen',
      icon: <ClickToListenIcon />,
      title: t('Click to Listen'),
      description: t('Start from any sentence')
    }
  ];

  const navigationSettingsConfig = [
    {
      key: 'appearance',
      icon: <AppearanceIcon />,
      title: t('Appearance'),
      description: isPDF ? t('Customize theme and cursor color') : t('Customize theme, font, and cursor color'),
      onClick: () => {
        setCurrentPanel(SettingsPanel.Appearance);
      }
    },
    {
      key: 'autoSkipContent',
      icon: <AutoSkipContentIcon />,
      title: t('Auto Skip Content'),
      description: t('Skip headers, footers, and more'),
      onClick: () => {
        setCurrentPanel(SettingsPanel.AutoSkipContent);
      }
    }
  ];

  const handleBack = () => {
    setCurrentPanel(SettingsPanel.Root);
  };

  return (
    <div className={cn('h-[calc(100%-53px)] flex flex-col mt-[53px] bg-bg-prim-w-110 box-border border-l border-l-brdr-prim-10-80', className)}>
      <div className="flex-grow flex flex-col overflow-hidden">
        {currentPanel === SettingsPanel.Root ? (
          <>
            <div className="flex bg-bg-prim-w-110 justify-between relative items-center px-spl-7 py-spl-6">
              <div className="text-subheading-4 grow text-left text-icn-txt-prim">{t('Settings')}</div>
              <button className="absolute right-spl-7 hover:opacity-75 active:opacity-50">
                <CloseIcon onClick={onClose} className="text-icn-txt-prim" />
              </button>
            </div>

            <SimpleBar className="flex-shrink min-h-0">
              <div className="flex flex-col">
                <div className="flex flex-col">
                  {toggleSettingsConfig.map(setting => (
                    <div key={setting.key} className="flex items-center justify-between px-spl-7 py-spl-6">
                      <div className="flex items-center gap-spl-6">
                        <div className="size-5">{setting.icon}</div>
                        <div className="flex flex-col">
                          <span className="text-subheading-5 text-icn-txt-prim">{setting.title}</span>
                          <span className="text-body-6 text-icn-txt-sec">{setting.description}</span>
                        </div>
                      </div>
                      <Toggle state={props[setting.key] ? 'checked' : 'default'} onChange={() => onSettingChange(setting.key, !props[setting.key])} />
                    </div>
                  ))}
                </div>

                <div className="flex flex-col">
                  {navigationSettingsConfig.map(setting => (
                    <button key={setting.key} className="group flex items-center justify-between px-spl-7 py-spl-6 w-full" onClick={setting.onClick}>
                      <div className="flex items-center gap-spl-6 relative">
                        <div className="size-5">{setting.icon}</div>
                        <div className="flex flex-col items-start">
                          <span className="text-subheading-5 text-icn-txt-prim">{setting.title}</span>
                          <span className="text-body-6 text-icn-txt-sec">{setting.description}</span>
                        </div>
                      </div>
                      <ChevronRightIcon className="size-4 text-icn-txt-quat group-hover:text-icn-txt-prim" />
                    </button>
                  ))}
                </div>
                <div className="h-32 w-full bg-transparent" />
              </div>
            </SimpleBar>
          </>
        ) : currentPanel === SettingsPanel.AutoSkipContent ? (
          <AutoSkipContentPanel
            autoSkipContentUnlocked={autoSkipContentUnlocked}
            autoSkipContentUnlockedReason={autoSkipContentUnlockedReason}
            onBack={handleBack}
            onClose={onClose}
            onAllSkipSettingsChange={onAllSkipSettingsChange}
            onAutoSkipContentChange={onAutoSkipContentChange}
            skipContentSettings={skipContentSettings}
          />
        ) : (
          <AppearancePanel isEPUB={isEPUB} isPDF={isPDF} onBack={handleBack} onClose={onClose} />
        )}
      </div>

      {isMobile() && (
        <div className="bg-surface-primary border-t border-border-primary p-spl-7 shadow-[0_-4px_8px_rgba(0,0,0,0.08)]">
          <div className="flex flex-col items-stretch gap-spl-7">
            <div className="flex flex-col items-center">
              <span className="text-subheading-5 text-icn-txt-prim">{t('Get the Speechify iOS App', { system: isIOS() ? 'iPhone' : 'Android' })}</span>
              <span className="text-body-6 text-icn-txt-sec">{t('The best way to listen and enjoy your files')}</span>
            </div>
            <DownloadMobileAppButton darkMode={darkMode} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
